import axios from 'axios';
import config from '../config.json';
import { logError, logInfo } from '../utils/logger';

const TOKEN_KEY = 'gym_tracker_auth_token';
const USER_KEY = 'gym_tracker_user';

if (!config.discord.bot_client_id) {
  throw new Error('Discord client ID is missing!');
}
if (!config.discord.bot_client_secret) {
  throw new Error('Discord client secret is missing!');
}
if (!config.discord.bot_token) {
  throw new Error('Discord client token is missing!');
}

class AuthService {
  constructor() {
    this.isAuthenticated = false;
    this.user = null;
    this.authError = false;
    this.loadFromStorage();
  }

  loadFromStorage() {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    
    if (token && userStr) {
      this.isAuthenticated = true;
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        logError('Failed to parse user data:', e);
        this.logout();
      }
    }
  }

  getAuthUrl() {
    const scope = 'identify guilds';
    return `https://discord.com/api/oauth2/authorize?client_id=${config.discord.bot_client_id}&redirect_uri=${encodeURIComponent(config.discord.redirect_uri)}&response_type=code&scope=${scope}`;
  }

  async handleCallback(code) {
    logInfo('handle callback code:', code);
	if (this.user && this.user.code == code && this.isAuthenticated) {
      logInfo('User already logged in with this code. Skipping.');
      return true;
	}
    if (this.authError) {
      logError('Previous authentication error detected. Debounce.');
      await new Promise(resolve => setTimeout(resolve, 30000));
    }
    try {
      const tokenResponse = await axios.post('https://discord.com/api/oauth2/token', new URLSearchParams({
        client_id: config.discord.bot_client_id,
        client_secret: config.discord.bot_client_secret,
        grant_type: 'authorization_code',
        code,
        redirect_uri: config.discord.redirect_uri,
        scope: 'identify guilds'
      }), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      const { access_token, token_type } = tokenResponse.data;
      
      // Get user details
      const userResponse = await axios.get('https://discord.com/api/users/@me', {
        headers: {
          Authorization: `${token_type} ${access_token}`
        }
      });

      // Check if user has the required role (this needs to be verified server-side)
      const hasRequiredRole = await this.verifyUserRole(userResponse.data.id);

      if (!hasRequiredRole) {
        throw new Error('You do not have the required role in the Discord server');
      }

      // Store user info and token
      const user = {
        id: userResponse.data.id,
        username: userResponse.data.username,
        avatar: userResponse.data.avatar,
        discriminator: userResponse.data.discriminator,
		code: code
      };
	  
	  logInfo('Successfully logged in.', user);

      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      
      this.isAuthenticated = true;
      this.user = user;
      return true;
    } catch (error) {
      logError('Authentication error:', error);
      this.authError = true;
      this.logout();
      await new Promise(resolve => setTimeout(resolve, 30000));
      throw error;
    }
  }

  async verifyUserRole(userId) {
    try {
      const response = await axios.get(`${this.getAPIUrl()}/verify-role/${userId}`, {
        headers: {
          Authorization: `Bearer ${config.api_bearer}`
        }
      });
      
      return response.data.hasRole;
    } catch (error) {
      console.error('Role verification error:', error);
      return false;
    }
  }
  
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.isAuthenticated = false;
    this.user = null;
  }

  getAPIUrl() {
    return `https://${config.server_host}:${config.server_port}`;
  }

  getUser() {
    return this.user;
  }

  getAPIBearer() {
    return config.api_bearer;
  }

  isLoggedIn() {
    return this.isAuthenticated;
  }
}

export default new AuthService();
